import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  runTransaction,
} from 'firebase/firestore';
import { db } from './config';
import { addStockBatch } from './stockBatchService';
import { addInventoryItem, getInventoryItems } from './inventoryService';
import { deductGRNStoreQty } from './grnService';

const COLLECTION = 'stockTransfers';

// Auto-generate Transfer number: TRF-001, TRF-002, ...
export const generateTransferNumber = async () => {
  try {
    const counterRef = doc(db, 'counters', 'transferNumber');
    const result = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      const next = counterDoc.exists() ? (counterDoc.data().current || 0) + 1 : 1;
      transaction.set(counterRef, { current: next }, { merge: true });
      return `TRF-${String(next).padStart(3, '0')}`;
    });
    return { success: true, transferNumber: result };
  } catch (error) {
    return { success: true, transferNumber: `TRF-${Date.now()}` };
  }
};

// Get all transfers
export const getStockTransfers = async () => {
  try {
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return {
      success: true,
      data: snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get single transfer
export const getStockTransferById = async (id) => {
  try {
    const snap = await getDoc(doc(db, COLLECTION, id));
    if (!snap.exists()) return { success: false, error: 'Transfer not found' };
    return { success: true, data: { id: snap.id, ...snap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Create a stock transfer.
 * Items are processed sequentially to prevent Firestore read-then-write race
 * conditions when multiple items share the same inventory document.
 */
export const createStockTransfer = async (transferData) => {
  try {
    const { transferNumber, grnId, grnNumber, supplier, transferDate, items, notes, createdBy } = transferData;

    // Load current inventory once — kept up-to-date via the local map below
    const invResult = await getInventoryItems();
    // Build a mutable map so newly-created items are visible to subsequent iterations
    const inventoryMap = new Map(
      (invResult.success ? invResult.data : []).map((inv) => [
        `${inv.name.toLowerCase()}||${(inv.category || '').toLowerCase()}`,
        inv,
      ])
    );

    const processedItems = [];

    // Process items SEQUENTIALLY to avoid concurrent read-modify-write conflicts
    // on the same inventory document (e.g. duplicate product rows in one transfer).
    for (const item of items) {
      const itemNameLower = item.name.toLowerCase();
      const itemCatLower = (item.category || '').toLowerCase();
      const mapKey = `${itemNameLower}||${itemCatLower}`;

      // Find existing inventory item by name + category
      let invItem = inventoryMap.get(mapKey);

      if (!invItem) {
        // New item: create inventory record with the first batch embedded atomically.
        // This is a single write — avoids the two-step create-then-patch-batch gap
        // where a network failure would leave an item with batches:[].
        const batchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newItemData = {
          name: item.name,
          category: item.category || 'Uncategorized',
          stock: item.qty,
          costPrice: item.costPrice,
          retailPrice: item.retailPrice,
          wholesalePrice: item.wholesalePrice,
          itemCode: item.itemCode || '',
          transferNumber: transferNumber,
          grnNumber: grnNumber,
          isInternal: !!item.isInternal,
          batches: [
            {
              batchId,
              purchaseInvoiceId: grnId,
              purchaseInvoiceNumber: grnNumber,
              transferNumber: transferNumber,
              purchaseDate: transferDate,
              costPrice: item.costPrice,
              retailPrice: item.retailPrice || 0,
              wholesalePrice: item.wholesalePrice || 0,
              discount: item.discount || 0,
              quantityPurchased: item.qty,
              quantityRemaining: item.qty,
              supplier: supplier || 'N/A',
              source: 'stock_transfer',
              createdAt: new Date().toISOString(),
            },
          ],
          averageCost: item.costPrice,
          lastCostPrice: item.costPrice,
        };

        const addResult = await addInventoryItem(newItemData);
        if (!addResult.success) throw new Error(`Failed to create inventory item: ${item.name}`);

        invItem = { id: addResult.id, ...newItemData };
        // Register in map so later iterations targeting the same item use addStockBatch
        inventoryMap.set(mapKey, invItem);

        processedItems.push({
          inventoryItemId: invItem.id,
          name: item.name,
          category: item.category || 'Uncategorized',
          itemCode: item.itemCode || '',
          qty: item.qty,
          costPrice: item.costPrice,
          retailPrice: item.retailPrice,
          wholesalePrice: item.wholesalePrice,
          batchId,
          itemIndex: item.itemIndex,
          isInternal: !!item.isInternal,
        });
      } else {
        // Existing item — update itemCode if missing
        if (item.itemCode && !invItem.itemCode) {
          const { updateInventoryItem } = await import('./inventoryService');
          await updateInventoryItem(invItem.id, { itemCode: item.itemCode });
          invItem = { ...invItem, itemCode: item.itemCode };
          inventoryMap.set(mapKey, invItem);
        }

        // Add FIFO batch — sequential execution ensures no read-write race
        const batchResult = await addStockBatch(invItem.id, {
          purchaseInvoiceId: grnId,
          purchaseInvoiceNumber: grnNumber,
          transferNumber: transferNumber,
          purchaseDate: transferDate,
          costPrice: item.costPrice,
          retailPrice: item.retailPrice,
          wholesalePrice: item.wholesalePrice,
          discount: item.discount || 0,
          quantity: item.qty,
          supplier: supplier,
          source: 'stock_transfer',
        });

        if (!batchResult.success) throw new Error(`Failed to create batch for: ${item.name}`);

        processedItems.push({
          inventoryItemId: invItem.id,
          name: item.name,
          category: item.category || 'Uncategorized',
          itemCode: item.itemCode || '',
          qty: item.qty,
          costPrice: item.costPrice,
          retailPrice: item.retailPrice,
          wholesalePrice: item.wholesalePrice,
          batchId: batchResult.batchId,
          itemIndex: item.itemIndex,
          isInternal: !!item.isInternal,
        });
      }
    }

    // Run GRN deduction and transfer record save in parallel
    const totalQty = items.reduce((s, i) => s + i.qty, 0);
    const totalValue = items.reduce((s, i) => s + i.qty * i.costPrice, 0);

    const [, docRef] = await Promise.all([
      deductGRNStoreQty(grnId, items.map((i) => ({ itemIndex: i.itemIndex, qty: i.qty }))),
      addDoc(collection(db, COLLECTION), {
        transferNumber,
        grnId,
        grnNumber,
        supplier,
        transferDate,
        items: processedItems,
        notes: notes || '',
        createdBy: createdBy || '',
        totalQty,
        totalValue,
        createdAt: new Date().toISOString(),
      }),
    ]);

    return { success: true, id: docRef.id, transferNumber, processedItems, totalQty, totalValue };
  } catch (error) {
    console.error('Error creating stock transfer:', error);
    return { success: false, error: error.message };
  }
};
